// SAM-624 — the pricing-v2 controls of the front-desk service booking step: rate-group picker
// (SAM-588), cabana/unit picker + unit info (SAM-591), event size band (SAM-592) and the season
// tag (SAM-589). Everything shown here comes from pricingPreview.ts and is DISPLAY ONLY — the
// request carries quantities, `rate_group` and `resource_key`, never a price.
//
// New component: Tailwind + tokens.css only (SAM-175 freeze).

import { CalendarRange, Home, Layers, Users } from 'lucide-react';
import type { BookingOption } from '../../../hooks/useServices';
import { Notice } from '../../../components/ui/Notice';
import { pricingErrorMessage } from '../../../utils/pricingErrors';
import { useT, type AdminI18n } from '../../../i18n/admin';
import { labelOf, type BookingRateGroup, type BookingService } from './bookingService';
import {
  groupCell, groupsForOption, isPreviewError, isPreviewOk, resourcesOf, requiresResourceKey, resolveUnit,
  type PreviewLine, type PreviewResult, type PreviewMode,
} from './pricingPreview';


const selectStyle = {
  height: 28, fontSize: 12, padding: '0 8px', borderRadius: 6,
  border: '1px solid var(--line)', background: '#fff', color: 'var(--ink-900)', maxWidth: '100%',
} as const;

// ─── Per-line price text ────────────────────────────────────────────────────

export function linePriceText(
  option: BookingOption,
  mode: PreviewMode,
  line: PreviewLine | undefined,
  fallbackPrice: number,
  currency: string,
  { t, fmt }: Pick<AdminI18n, 't' | 'fmt'>,
): string {
  const money = fmt.money;
  if (mode === 'event') return t('booking.price.flatEvent');
  if (mode === 'unit') {
    const extra = line?.unitPrice ?? option.extra_guest_price ?? 0;
    return t('booking.price.perExtraGuest', { price: money(extra, currency) });
  }
  const price = line?.unitPrice ?? fallbackPrice;
  return t('booking.price.perUnit', { price: money(price, currency) });
}

// ─── Rate group picker ──────────────────────────────────────────────────────

type RateGroupPickerProps = {
  option: BookingOption;
  groups: BookingRateGroup[];
  /** The group the line is currently priced in (picked or defaulted); null = must choose. */
  value: string | null;
  onChange: (groupId: string) => void;
};

export const RateGroupPicker = ({ option, groups, value, onChange }: RateGroupPickerProps) => {
  const { t, lang, fmt } = useT();
  const money = fmt.money;
  const available = groupsForOption(option, groups);
  if (available.length === 0) return null;
  const optionLabel = labelOf({ en: option.label_en, es: option.label_es }, option.id, lang);

  if (available.length === 1) {
    const only = available[0];
    return (
      <div className="text-[11px] mt-1" style={{ color: 'var(--ink-500)' }}>
        {t('booking.rateGroup.label')}: <strong style={{ color: 'var(--ink-700)' }}>{labelOf(only.label, only.id, lang)}</strong>
      </div>
    );
  }

  return (
    <label className="flex items-center gap-2 mt-1.5 text-[11px]" style={{ color: 'var(--ink-500)' }}>
      <Layers size={12} aria-hidden />
      <span>{t('booking.rateGroup.label')}</span>
      <select
        aria-label={t('booking.rateGroup.for', { option: optionLabel })}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        style={selectStyle}
      >
        {value === null && <option value="" disabled>{t('booking.rateGroup.choose')}</option>}
        {available.map((g) => (
          <option key={g.id} value={g.id}>
            {labelOf(g.label, g.id, lang)} · {money(groupCell(option, g.id) ?? 0, option.currency)}
          </option>
        ))}
      </select>
      {value === null && <span style={{ color: 'var(--danger)' }}>{t('common.required')}</span>}
    </label>
  );
};

// ─── Cabana / unit ──────────────────────────────────────────────────────────

type UnitPanelProps = {
  service: BookingService;
  resourceKey: string | null;
  onResourceChange: (key: string | null) => void;
  preview: PreviewResult;
};

export const UnitPanel = ({ service, resourceKey, onResourceChange, preview }: UnitPanelProps) => {
  const { t, fmt } = useT();
  const money = fmt.money;
  const resources = resourcesOf(service);
  const needsPick = requiresResourceKey(service);
  const currency = service.booking_options?.[0]?.currency || 'USD';

  // Each resource's own price/included/max (metadata override on top of pricing.unit), for the
  // picker labels; a resource whose config cannot be resolved is still listed by name.
  const describe = (key: string) => {
    try {
      const u = resolveUnit(service, key);
      const base = t('booking.unit.describe', { price: money(u.price, currency), included: u.includedPax });
      return u.maxPax ? `${base} · ${t('booking.unit.max', { max: u.maxPax })}` : base;
    } catch {
      return t('booking.unit.priceNotSet');
    }
  };

  const unit = isPreviewOk(preview) ? preview.unit : undefined;

  return (
    <div className="mt-3 rounded-lg px-3 py-2.5" style={{ border: '1px solid var(--line)', background: 'var(--surface-2)' }}>
      <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: 'var(--ink-900)' }}>
        <Home size={13} aria-hidden /> {t('booking.unit.title')}
      </div>

      {needsPick && (
        <label className="block mt-2 text-[11px]" style={{ color: 'var(--ink-500)' }}>
          {t('booking.unit.whichOne')}
          <select
            aria-label={t('booking.unit.ariaLabel')}
            value={resourceKey ?? ''}
            onChange={(e) => onResourceChange(e.target.value || null)}
            className="block mt-1"
            style={{ ...selectStyle, height: 32, fontSize: 13, width: '100%' }}
          >
            <option value="" disabled>{t('booking.unit.select')}</option>
            {resources.map((r) => (
              <option key={r.key} value={r.key}>{r.name || r.key} — {describe(r.key)}</option>
            ))}
          </select>
        </label>
      )}

      {unit && (
        <div className="mt-2 text-[12px]" style={{ color: 'var(--ink-700)' }}>
          <div>
            <strong>{unit.resourceName || t('booking.unit.fallbackName')}</strong>:{' '}
            {t(unit.includedPax === 1 ? 'booking.unit.includesOne' : 'booking.unit.includesMany', { price: money(unit.price, currency), n: unit.includedPax })}
            {unit.maxPax ? t('booking.unit.upTo', { max: unit.maxPax }) : ''}.
          </div>
          <div className="mt-0.5" style={{ color: 'var(--ink-500)' }}>
            {t(unit.pax === 1 ? 'booking.guests.one' : 'booking.guests.many', { n: unit.pax })} · {unit.extraPax > 0
              ? t(unit.extraPax === 1 ? 'booking.unit.extraOne' : 'booking.unit.extraMany', { n: unit.extraPax })
              : t('booking.unit.allIncluded')}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Event size ─────────────────────────────────────────────────────────────

export const EventPanel = ({ preview, pax, currency }: { preview: PreviewResult; pax: number; currency: string }) => {
  const { t, fmt } = useT();
  const money = fmt.money;
  const event = isPreviewOk(preview) ? preview.event : undefined;
  return (
    <div className="mt-3 rounded-lg px-3 py-2.5" style={{ border: '1px solid var(--line)', background: 'var(--surface-2)' }}>
      <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: 'var(--ink-900)' }}>
        <Users size={13} aria-hidden /> {t('booking.event.size')}
      </div>
      {event ? (
        <div className="mt-1.5 text-[12px]" style={{ color: 'var(--ink-700)' }}>
          {t('booking.event.attendeesToSize', { n: event.pax })} <strong>{event.band.toUpperCase()}</strong> ({event.bandLabel}):{' '}
          <strong>{money(event.price, currency)}</strong> {t('booking.event.flatOnce')}
        </div>
      ) : (
        <div className="mt-1.5 text-[12px]" style={{ color: 'var(--ink-500)' }}>
          {pax > 0 ? t('booking.event.noPrice') : t('booking.event.setCount')}
        </div>
      )}
    </div>
  );
};

// ─── Season tag + preview problems ──────────────────────────────────────────

export const SeasonTag = ({ preview, service }: { preview: PreviewResult; service?: BookingService }) => {
  const { t, lang } = useT();
  if (!preview.season) return null;
  // The preview carries the English label; the service's own {en, es} label gives the active language.
  const raw = (service?.pricing?.seasons as Array<{ id: string; label?: { en?: string | null; es?: string | null } }> | undefined)
    ?.find((s) => s.id === preview.season?.id)?.label;
  const label = raw ? labelOf(raw, preview.season.label, lang) : preview.season.label;
  return (
    <div className="inline-flex items-center gap-1.5 text-[11px] mt-2 px-2 py-0.5 rounded-full"
      style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--ink-700)' }}>
      <CalendarRange size={11} aria-hidden /> {t('booking.season.pricesApply', { season: label })}
    </div>
  );
};

export const PreviewProblem = ({ preview }: { preview: PreviewResult }) => {
  const { lang } = useT();
  if (!isPreviewError(preview)) return null;
  // SAM-683: the bilingual message for this error_code, in the active language only.
  const text = pricingErrorMessage(preview.code, preview.details)?.[lang];
  if (!text) return null;
  return <Notice variant="warn" className="mt-3">{text}</Notice>;
};
